import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { PenLine, Plus, BookOpen, Heart, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Journal() {
    const [journals, setJournals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [viewEntry, setViewEntry] = useState(null);
    
    // Form state
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [gratitude, setGratitude] = useState('');
    const [prayerRequest, setPrayerRequest] = useState('');
    const [scriptureReference, setScriptureReference] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchJournals();
    }, []);

    const fetchJournals = async () => {
        try {
            const response = await axios.get(`${API_URL}/journals`);
            setJournals(response.data);
        } catch (error) {
            toast.error('Failed to load journal entries');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            const response = await axios.post(`${API_URL}/journals`, {
                title,
                content,
                gratitude: gratitude || null,
                prayer_request: prayerRequest || null,
                scripture_reference: scriptureReference || null
            });
            
            setJournals([response.data, ...journals]);
            toast.success('Journal entry saved!');
            resetForm();
            setIsDialogOpen(false);
        } catch (error) {
            toast.error('Failed to save journal entry');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${API_URL}/journals/${id}`);
            setJournals(journals.filter(j => j.id !== id));
            setViewEntry(null);
            toast.success('Entry deleted');
        } catch (error) {
            toast.error('Failed to delete entry');
        }
    };

    const resetForm = () => {
        setTitle('');
        setContent('');
        setGratitude('');
        setPrayerRequest('');
        setScriptureReference('');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
                <div className="loading-spinner" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FDFCF8]" data-testid="journal-page">
            <Navigation />
            
            {/* Hero Section */}
            <div className="relative h-48 md:h-64 overflow-hidden">
                <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url('https://images.unsplash.com/photo-1550135530-dcc2e8498696?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxvcGVuJTIwYmlibGUlMjBvbiUyMHRhYmxlJTIwY29mZmVlJTIwY3VwJTIwc3VubGlnaHQlMjBhZXN0aGV0aWN8ZW58MHx8fHwxNzY3Mjc5NzA5fDA&ixlib=rb-4.1.0&q=85')`
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#FDFCF8]/60 to-[#FDFCF8]" />
                <div className="container-app relative h-full flex items-end pb-6">
                    <div>
                        <h1 className="font-display text-3xl md:text-4xl font-medium text-[#2D2A26]">
                            My Journal
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Reflect, pray, and grow in your faith
                        </p>
                    </div>
                </div>
            </div>

            <main className="container-app py-8">
                {/* New Entry Button */}
                <div className="flex justify-end mb-8">
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="btn-primary" data-testid="new-journal-btn">
                                <Plus className="w-5 h-5 mr-2" />
                                New Entry
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl">New Journal Entry</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                                <div className="space-y-2">
                                    <Label htmlFor="title">Title</Label>
                                    <Input
                                        id="title"
                                        data-testid="journal-title-input"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="Today's reflection..."
                                        required
                                        className="bg-white/50"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="content">Your Thoughts</Label>
                                    <Textarea
                                        id="content"
                                        data-testid="journal-content-input"
                                        value={content}
                                        onChange={(e) => setContent(e.target.value)}
                                        placeholder="What's on your heart today?"
                                        required
                                        rows={6}
                                        className="bg-white/50 resize-none"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="gratitude" className="flex items-center gap-2">
                                        <Heart className="w-4 h-4 text-primary" />
                                        Gratitude
                                    </Label>
                                    <Textarea
                                        id="gratitude"
                                        data-testid="journal-gratitude-input"
                                        value={gratitude}
                                        onChange={(e) => setGratitude(e.target.value)}
                                        placeholder="What are you thankful for today?"
                                        rows={3}
                                        className="bg-white/50 resize-none"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="prayer">Prayer Request</Label>
                                    <Textarea
                                        id="prayer"
                                        data-testid="journal-prayer-input"
                                        value={prayerRequest}
                                        onChange={(e) => setPrayerRequest(e.target.value)}
                                        placeholder="What would you like to pray for?"
                                        rows={3}
                                        className="bg-white/50 resize-none"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="scripture" className="flex items-center gap-2">
                                        <BookOpen className="w-4 h-4 text-primary" />
                                        Scripture Reference
                                    </Label>
                                    <Input
                                        id="scripture"
                                        data-testid="journal-scripture-input"
                                        value={scriptureReference}
                                        onChange={(e) => setScriptureReference(e.target.value)}
                                        placeholder="e.g., Psalm 23:1"
                                        className="bg-white/50"
                                    />
                                </div>

                                <div className="flex gap-3 justify-end pt-4">
                                    <Button 
                                        type="button" 
                                        variant="ghost"
                                        onClick={() => setIsDialogOpen(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        className="btn-primary"
                                        disabled={submitting}
                                        data-testid="save-journal-btn"
                                    >
                                        {submitting ? 'Saving...' : 'Save Entry'}
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Journal List */}
                {journals.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                            <PenLine className="w-8 h-8 text-muted-foreground" />
                        </div>
                        <h3 className="font-display text-xl text-[#2D2A26] mb-2">
                            Start Your Journey
                        </h3>
                        <p className="text-muted-foreground mb-6">
                            Your first journal entry awaits
                        </p>
                        <Button 
                            onClick={() => setIsDialogOpen(true)}
                            className="btn-primary"
                        >
                            Write Your First Entry
                        </Button>
                    </div>
                ) : (
                    <div className="journal-list">
                        {journals.map((entry, index) => (
                            <div 
                                key={entry.id}
                                className={`journal-card cursor-pointer animate-fade-in stagger-${Math.min(index + 1, 6)}`}
                                onClick={() => setViewEntry(entry)}
                                data-testid={`journal-entry-${index}`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <p className="text-xs text-muted-foreground mb-1">
                                            {format(new Date(entry.created_at), 'MMMM d, yyyy')}
                                        </p>
                                        <h3 className="font-display text-lg text-[#2D2A26] mb-2">
                                            {entry.title}
                                        </h3>
                                        <p className="text-muted-foreground line-clamp-2">
                                            {entry.content}
                                        </p>
                                    </div>
                                    {entry.scripture_reference && (
                                        <span className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-full ml-4">
                                            {entry.scripture_reference}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* View Entry Dialog */}
                <Dialog open={!!viewEntry} onOpenChange={() => setViewEntry(null)}>
                    <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                        {viewEntry && (
                            <>
                                <DialogHeader>
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <p className="text-xs text-muted-foreground mb-1">
                                                {format(new Date(viewEntry.created_at), 'MMMM d, yyyy')}
                                            </p>
                                            <DialogTitle className="font-display text-2xl">
                                                {viewEntry.title}
                                            </DialogTitle>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="text-muted-foreground hover:text-destructive"
                                            onClick={() => handleDelete(viewEntry.id)}
                                            data-testid="delete-journal-btn"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </DialogHeader>
                                
                                <div className="space-y-6 mt-4">
                                    <div>
                                        <p className="text-[#2D2A26] leading-relaxed whitespace-pre-wrap">
                                            {viewEntry.content}
                                        </p>
                                    </div>

                                    {viewEntry.gratitude && (
                                        <div className="bg-primary/5 rounded-2xl p-4">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Heart className="w-4 h-4 text-primary" />
                                                <span className="text-sm font-medium text-primary">Gratitude</span>
                                            </div>
                                            <p className="text-[#2D2A26]">{viewEntry.gratitude}</p>
                                        </div>
                                    )}

                                    {viewEntry.prayer_request && (
                                        <div className="bg-accent/10 rounded-2xl p-4">
                                            <p className="text-sm font-medium text-accent mb-2">Prayer Request</p>
                                            <p className="text-[#2D2A26]">{viewEntry.prayer_request}</p>
                                        </div>
                                    )}

                                    {viewEntry.scripture_reference && (
                                        <div className="flex items-center gap-2">
                                            <BookOpen className="w-4 h-4 text-muted-foreground" />
                                            <span className="text-muted-foreground">
                                                {viewEntry.scripture_reference}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </DialogContent>
                </Dialog>
            </main>
        </div>
    );
}
